import {
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import {
  chakra,
  Box,
  Button,
  Column,
  ChevronDownIcon,
  CloseIcon,
  PlusIcon,
  ProjectsIcon,
  TextWithTooltip,
  UnlinkIcon,
  CheckIcon,
  IconButton,
  MenuItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { CanvasReviewTaskPicker } from "src/pages/projects/library/canvas/canvas-review-task-picker";
import { Link } from "src/router";
import { PROJECTS_PATHS } from "src/pages/projects/constants";
import {
  StatusIcon,
  CATEGORY_ICONS,
} from "src/pages/projects/components/status-icon";
import { StatusMenu } from "src/pages/projects/components/status-menu";
import { useCloseOnPressOutside } from "src/components/comments/use-close-on-press-outside";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import {
  cappedScreenPx,
  screenPx,
} from "src/pages/projects/library/canvas/screen-px";
import { canvasBoundary } from "src/pages/projects/library/canvas/canvas-bar-placement";
import { useCanvasReview } from "src/pages/projects/library/canvas/canvas-review-context";
import type {
  ReviewFile,
  ReviewLinkedProject,
  ReviewTask,
} from "src/pages/projects/library/canvas/canvas-review";
import {
  STATUS_CATEGORY_LABELS,
  sortStatuses,
  taskCategory,
  SUBTASK_CATEGORIES,
} from "src/pages/projects/utils";

const ProjectButton = chakra(Button);

// On the text's baseline, as the status menu's chevron sits, so the two triggers match.
function BaselineChevron(props: ComponentProps<typeof ChevronDownIcon>) {
  return <ChevronDownIcon {...props} baseline />;
}

export function CanvasFileReview({
  file,
  isRevealed = true,
}: {
  file: ReviewFile;
  /** When false, Mark as ready stays hidden until its card is hovered or keyboard focused. */
  isRevealed?: boolean;
}) {
  const [isLinking, setLinking] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const linkButtonRef = useRef<HTMLButtonElement>(null);
  const pressInside = useCloseOnPressOutside(
    isOpen
      ? () => {
          setOpen(false);
          setLinking(false);
        }
      : null,
  );
  const scrollFade = useScrollFade();
  const review = useCanvasReview();
  if (!review) return null;
  const tasks = review.tasks.get(file.id) ?? [];
  const projects = review.projects.get(file.id) ?? [];
  const firstTask = tasks[0];
  const linkCount = tasks.length + projects.length;
  const moveTask = (task: ReviewTask) => {
    if (!statusButtonRef.current) return;
    review.changeProject(file, task, statusButtonRef.current, () =>
      setOpen(true),
    );
    setOpen(false);
  };
  return (
    <Row
      {...(isOpen ? pressInside : {})}
      className="nodrag nopan"
      position="relative"
      zIndex={2}
      flexShrink={0}
      gap={1}
      // Holding only a hidden Mark as ready, it lets a press reach the card; the button takes pointer events back when it shows.
      pointerEvents={!tasks.length && !isRevealed ? "none" : undefined}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      {!tasks.length && (
        <Box
          // Opacity keeps its room, so the reveal never shifts the name row.
          opacity={isRevealed ? 1 : 0}
          // Hidden, a press falls through to the card and selects it, which reveals the button, rather than submitting unseen.
          pointerEvents={isRevealed ? undefined : "none"}
          transitionProperty="opacity"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          _groupHover={{ opacity: 1, pointerEvents: "auto" }}
          sx={{
            // No pseudo prop supports :has(:focus-visible).
            "[data-group]:has(:focus-visible) &": {
              opacity: 1,
              pointerEvents: "auto",
            },
          }}
        >
          <Tooltip
            message={
              review.error
                ? "Could not load linked tasks. Refresh to try again."
                : "Mark as ready"
            }
          >
            <IconButton
              size="sm"
              shape="circle"
              variant="tertiary"
              icon={CheckIcon}
              aria-label="Mark as ready"
              // Shrinks with the name row when zoomed out, so it never spills onto the card, and stops at 24px on screen when zoomed in. The card carries the zoom while hovered or selected, which is when this shows; in the header it falls back to 1.
              width={cappedScreenPx(24)}
              height={cappedScreenPx(24)}
              border={`${screenPx(1)} dashed`}
              borderColor="gray.500"
              // A translucent wash shows on any canvas ground, where the ghost variant's gray.100 disappears.
              _hover={{ bg: "blackAlpha.08" }}
              _active={{ bg: "blackAlpha.12" }}
              sx={{
                // The ghost variant colors the svg itself, so no color prop reaches the glyph. Its hover and disabled svg rules still win.
                "& svg": { boxSize: cappedScreenPx(12), color: "gray.500" },
              }}
              // A 32px pointer target around the 24px circle.
              _after={{
                content: '""',
                position: "absolute",
                inset: `max(-4px, ${screenPx(-4)})`,
                borderRadius: "full",
              }}
              isLoading={review.isLoading}
              isDisabled={
                review.isBusy || review.isLoading || Boolean(review.error)
              }
              onClick={(event) => review.submit([file], event.currentTarget)}
            />
          </Tooltip>
        </Box>
      )}
      {firstTask && (
        <Popover
          isLazy
          isOpen={isOpen}
          onOpen={() => setOpen(true)}
          // Status menus render in a portal, so moving focus into them must keep the details open.
          closeOnBlur={false}
          placement="bottom-start"
          strategy="fixed"
          boundary={canvasBoundary(statusButtonRef.current)}
          onClose={() => {
            setOpen(false);
            setLinking(false);
          }}
        >
          <PopoverTrigger>
            <Button
              ref={statusButtonRef}
              size="sm"
              shape="circle"
              variant="tertiary"
              aria-label={`Linked work for ${file.name}: ${linkCount}`}
            >
              <ReviewStatusLabel task={firstTask}>
                {linkCount > 1 && (
                  <Text size="sm" color="text.secondary">
                    +{linkCount - 1}
                  </Text>
                )}
              </ReviewStatusLabel>
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              width="sm"
              maxWidth="calc(100vw - var(--chakra-space-8))"
              borderRadius="4xl"
            >
              <PopoverBody p={3}>
                <Column gap={3}>
                  <Row
                    justify="space-between"
                    align="center"
                    // On the rows' content edge, so the title, checks and labels share one left line.
                    px={3}
                  >
                    <Row gap={1}>
                      <Text fontWeight="medium">Linked work</Text>
                      <Text color="text.secondary">{linkCount}</Text>
                    </Row>
                    <IconButton
                      ref={linkButtonRef}
                      shape="circle"
                      size="xs"
                      variant="tertiary"
                      icon={isLinking ? CloseIcon : PlusIcon}
                      aria-label={
                        isLinking ? "Stop linking" : "Link to existing task"
                      }
                      aria-expanded={isLinking}
                      isDisabled={review.isBusy}
                      onClick={() => setLinking(!isLinking)}
                    />
                  </Row>
                  {/* Outside the scroll, so the task the chip shows never scrolls away. */}
                  <Column gap={1}>
                    <Box px={3}>
                      <Text size="sm" color="text.secondary">
                        Review
                      </Text>
                    </Box>
                    <ReviewTaskRow
                      fileId={file.id}
                      task={firstTask}
                      onMove={moveTask}
                    />
                  </Column>
                  {linkCount > 1 && (
                    <>
                      <Box
                        // Edge to edge: out through the body's padding.
                        mx={-3}
                        borderTopWidth="hairline"
                        borderColor="base.divider"
                      />
                      <Column
                        ref={scrollFade.ref}
                        sx={scrollFade.sx}
                        maxHeight={56}
                        overflowY="auto"
                      >
                        {tasks.slice(1).map((task) => (
                          <LinkedWorkRow
                            key={task.id}
                            icon={<TaskStatusIcon task={task} />}
                            title={task.title}
                            description={task.project?.title}
                            // The row shows status without a menu, so its name carries it to screen readers.
                            label={`${task.title}, ${taskStatusName(task)}`}
                            href={PROJECTS_PATHS.projectTask(
                              task.project_id,
                              task.id,
                            )}
                            onUnlink={() => review.unlink(file.id, task.id)}
                          />
                        ))}
                        {projects.map((project) => (
                          <LinkedProjectRow
                            key={project.id}
                            fileId={file.id}
                            project={project}
                          />
                        ))}
                      </Column>
                    </>
                  )}
                  {isLinking && (
                    <>
                      <Box
                        // Edge to edge: out through the body's padding.
                        mx={-3}
                        borderTopWidth="hairline"
                        borderColor="base.divider"
                      />
                      {/* Below the linked work: a new link joins the end of the list, and the review task stays the one the card shows. */}
                      <CanvasReviewTaskPicker
                        file={file}
                        onClose={() => {
                          setLinking(false);
                          linkButtonRef.current?.focus();
                        }}
                      />
                    </>
                  )}
                </Column>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </Row>
  );
}

function ReviewTaskRow({
  fileId,
  task,
  onMove,
}: {
  fileId: string;
  task: ReviewTask;
  onMove: (task: ReviewTask) => void;
}) {
  const review = useCanvasReview();
  if (!review) return null;
  return (
    <Column
      data-group
      gap={3}
      p={3}
      position="relative"
      borderRadius="3xl"
      _hover={{ bg: "base.background" }}
      sx={{
        // No pseudo prop supports :has(:focus-visible).
        "&:has(:focus-visible)": {
          bg: "base.background",
        },
      }}
    >
      <Box
        as={Link}
        href={PROJECTS_PATHS.projectTask(task.project_id, task.id)}
        position="absolute"
        inset={0}
        borderRadius="inherit"
        aria-label={task.title}
        _focusVisible={{ boxShadow: "outline" }}
      />
      <Row gap={2} align="start">
        <Box
          as={CheckIcon}
          boxSize={4}
          flexShrink={0}
          // On the title's first line: htui icons center themselves in a row otherwise.
          alignSelf="flex-start"
          mt={0.5}
          // The check draws 3px inside its box; this puts its ink on the content edge and keeps the title in place.
          ml={-0.5}
          mr={0.5}
          color="text.secondary"
        />
        <Box flex={1} minWidth={0}>
          <Text color="text.primary" fontWeight="medium">
            {task.title}
          </Text>
        </Box>
        <UnlinkButton
          title={task.title}
          onClick={() => review.unlink(fileId, task.id)}
        />
      </Row>
      <Row justify="space-between" align="center" gap={3}>
        <Box minWidth={0} position="relative">
          <Tooltip
            isDisabled={!task.parent_task_id}
            message={
              task.parent_task_id
                ? "Move the parent task to move this subtask"
                : undefined
            }
          >
            <ProjectButton
              size="sm"
              shape="circle"
              variant="tertiary"
              directionIcon={BaselineChevron}
              minWidth={0}
              maxWidth="100%"
              aria-label={`Move ${task.title} to another project`}
              isDisabled={review.isBusy || Boolean(task.parent_task_id)}
              onClick={() => onMove(task)}
            >
              <TextWithTooltip
                size="sm"
                color="text.secondary"
                fontWeight="normal"
              >
                {task.project?.title}
              </TextWithTooltip>
            </ProjectButton>
          </Tooltip>
        </Box>
        <Row gap={1} flexShrink={0} position="relative" zIndex={1}>
          <TaskStatus task={task} />
        </Row>
      </Row>
    </Column>
  );
}

/** One row of linked work below the review task: it opens the work, and Unlink shows on hover or focus. */
function LinkedWorkRow({
  icon,
  title,
  description,
  label,
  href,
  onUnlink,
}: {
  icon: ReactNode;
  title: string;
  /** A quiet second line under the title, as a task shows its project. */
  description?: string;
  label: string;
  href: string;
  onUnlink: () => void;
}) {
  return (
    <Row
      data-group
      align="start"
      gap={2}
      p={3}
      position="relative"
      flexShrink={0}
      borderRadius="3xl"
      _hover={{ bg: "base.background" }}
      sx={{
        // No pseudo prop supports :has(:focus-visible).
        "&:has(:focus-visible)": {
          bg: "base.background",
        },
      }}
    >
      <Box
        as={Link}
        href={href}
        position="absolute"
        inset={0}
        borderRadius="inherit"
        aria-label={label}
        _focusVisible={{ boxShadow: "outline" }}
      />
      <Box
        flexShrink={0}
        display="flex"
        // On the title's line.
        mt={0.5}
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <TextWithTooltip fontWeight="medium">{title}</TextWithTooltip>
        {description && (
          <TextWithTooltip size="sm" color="text.secondary">
            {description}
          </TextWithTooltip>
        )}
      </Box>
      <UnlinkButton title={title} onClick={onUnlink} />
    </Row>
  );
}

function LinkedProjectRow({
  fileId,
  project,
}: {
  fileId: string;
  project: ReviewLinkedProject;
}) {
  const review = useCanvasReview();
  if (!review) return null;
  return (
    <LinkedWorkRow
      icon={<Box as={ProjectsIcon} boxSize={4} color="icon.secondary" />}
      title={project.title}
      label={project.title}
      href={PROJECTS_PATHS.project(project.id)}
      onUnlink={() => review.unlinkProject(fileId, project.id)}
    />
  );
}

/** Unlink for a row in the linked work, shown on the row's hover or keyboard focus. */
function UnlinkButton({
  title,
  onClick,
}: {
  title: string;
  onClick: () => void;
}) {
  const review = useCanvasReview();
  return (
    <Box
      position="relative"
      flexShrink={0}
      opacity={0}
      _groupHover={{ opacity: 1 }}
      sx={{
        // Reveal keyboard actions without preserving pointer focus.
        "[data-group]:has(:focus-visible) &": {
          opacity: 1,
        },
      }}
    >
      <Tooltip message={`Unlink ${title}`}>
        <IconButton
          shape="circle"
          size="sm"
          variant="tertiary"
          icon={UnlinkIcon}
          aria-label={`Unlink ${title}`}
          isDisabled={Boolean(review?.isBusy)}
          onClick={onClick}
        />
      </Tooltip>
    </Box>
  );
}

function TaskStatusIcon({ task }: { task: ReviewTask }) {
  return (
    <StatusIcon
      status={task.status}
      category={task.status ? undefined : taskCategory(task)}
    />
  );
}

function taskStatusName(task: ReviewTask) {
  return task.status?.name ?? STATUS_CATEGORY_LABELS[taskCategory(task)];
}

/** A task's status as the card's chip draws it. */
function ReviewStatusLabel({
  task,
  children,
}: {
  task: ReviewTask;
  /** Trails the status, as the chip's count of the other linked tasks. */
  children?: ReactNode;
}) {
  return (
    <Row gap={1} align="center">
      <TaskStatusIcon task={task} />
      <Text size="sm">{taskStatusName(task)}</Text>
      {children}
    </Row>
  );
}

function TaskStatus({ task }: { task: ReviewTask }) {
  const review = useCanvasReview();
  if (!review) return null;
  return (
    <Box {...(review.isBusy ? { inert: "" } : {})}>
      <StatusMenu
        // Drawn as the card's chip, so the chip reads as the top task's status.
        appearance="chip"
        valueDisplay={<ReviewStatusLabel task={task} />}
        options={
          task.status
            ? sortStatuses(review.statuses(task.status.workflow_id)).map(
                (status) => ({
                  value: status.id,
                  label: status.name,
                  icon: () => <StatusIcon status={status} />,
                }),
              )
            : SUBTASK_CATEGORIES.map((category) => ({
                value: category,
                label: STATUS_CATEGORY_LABELS[category],
                icon: CATEGORY_ICONS[category],
              }))
        }
        value={task.status_id ?? taskCategory(task)}
        fallback={{
          label: taskStatusName(task),
          icon: () => <StatusIcon status={task.status} />,
        }}
        onChange={(statusId) => {
          if (!task.parent_task_id) review.setStatus(task.id, statusId);
          else {
            const category = SUBTASK_CATEGORIES.find(
              (value) => value === statusId,
            );
            if (category) review.setSubtaskStatus(task.id, category);
          }
        }}
      />
    </Box>
  );
}

/** Marks the selected files with no task yet for review. The project picker opens at `anchor`, since a menu item is gone once its menu closes. */
export function CanvasReviewSelectionItem({
  files,
  anchor,
}: {
  files: ReviewFile[];
  anchor: RefObject<HTMLElement>;
}) {
  const review = useCanvasReview();
  if (!review) return null;
  const eligible = files.filter((file) => !review.tasks.has(file.id));
  return (
    <MenuItem
      icon={CheckIcon}
      isDisabled={
        !eligible.length ||
        review.isBusy ||
        review.isLoading ||
        Boolean(review.error)
      }
      onClick={() => {
        if (anchor.current) review.submit(eligible, anchor.current);
      }}
    >
      Mark for review
    </MenuItem>
  );
}
